import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { StoreDispatch } from '../../store/configure-store'
import { FormInputText } from '../forms/input/form-input-text'
import { Backlog } from '../util-cmps/backlog'
import { Spinner } from '../util-cmps/spinner'
import { setUserMsg } from '../../store/user'
import { httpService } from '../../services/http-service'
import { getAuditControlsByCompanyNameQuery } from '../util-cmps/graphql-queries/audit-controls-query'
import { useEscapeKey } from '../../hooks/use-escape-key'
import { useScrollLock } from '../../hooks/use-scroll-lock'
import { useFocusTrap } from '../../hooks/use-focus-trap'

interface RoleEntry {
    role_id: string
    audit_control_id?: number | null
}

interface AuditControlOption {
    id: number
    systemName: string
    objectiveName: string
    entityName: string
}

const AVAILABLE_ROLES = ['ac-read', 'ac-write', 'control-owner', 'overall-read', 'overall-write']

const requiresAuditControl = (roleId: string) => !roleId.startsWith('overall')

interface UserFormModalProps {
    companyName: string
    userToEdit?: {
        email: string
        firstName: string
        lastName: string
        dbRoles: string[]
        roleDetails: { role: string; audit_control_id: number; system_name: string; objective_name: string; entity_name: string }[]
    } | null
    onSuccess: () => void
    onClose: () => void
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
    companyName, userToEdit, onSuccess, onClose
}) => {
    const isEditMode = !!userToEdit
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [email, setEmail] = useState(userToEdit?.email || '')
    const [firstName, setFirstName] = useState(userToEdit?.firstName || '')
    const [lastName, setLastName] = useState(userToEdit?.lastName || '')

    // Roles
    const [roleEntries, setRoleEntries] = useState<RoleEntry[]>(() => {
        if (userToEdit?.roleDetails?.length) {
            return userToEdit.roleDetails.map(rd => ({
                role_id: rd.role,
                audit_control_id: rd.audit_control_id || null,
            }))
        }
        if (userToEdit?.dbRoles?.length) {
            return userToEdit.dbRoles.map(r => ({ role_id: r, audit_control_id: null }))
        }
        return []
    })

    // Audit controls for control-owner assignment dropdown
    const [auditControls, setAuditControls] = useState<AuditControlOption[]>([])

    // Cognito (create mode only)
    const [createInCognito, setCreateInCognito] = useState(false)
    const [cognitoPassword, setCognitoPassword] = useState('')
    const generatePassword = () => {
        const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
        const lower = 'abcdefghjkmnpqrstuvwxyz'
        const digits = '23456789'
        const special = '!@#$%'
        const all = upper + lower + digits + special
        const required = [
            upper[Math.floor(Math.random() * upper.length)],
            lower[Math.floor(Math.random() * lower.length)],
            digits[Math.floor(Math.random() * digits.length)],
            special[Math.floor(Math.random() * special.length)],
        ]
        for (let i = required.length; i < 12; i++) required.push(all[Math.floor(Math.random() * all.length)])
        for (let i = required.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [required[i], required[j]] = [required[j], required[i]]
        }
        return required.join('')
    }
    const [suggestedPassword] = useState(generatePassword)

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()

    // Fetch audit controls for the dropdown (reuse existing GraphQL query)
    const fetchAuditControls = useCallback(async () => {
        try {
            const res = await httpService.graphql(companyName, getAuditControlsByCompanyNameQuery(companyName))
            const acData = res.auditControlsByCompanyName || []
            setAuditControls(acData.map((ac: any) => ({
                id: ac.id,
                systemName: ac.control?.mainSystem?.system?.name || '',
                objectiveName: ac.control?.objective?.name || '',
                entityName: ac.audit?.entity?.name || '',
            })))
        } catch { /* ignore */ }
    }, [companyName])

    useEffect(() => { fetchAuditControls() }, [fetchAuditControls])

    const acIdSet = new Set(auditControls.map(ac => ac.id))

    const addRoleEntry = () => {
        setRoleEntries(prev => [...prev, { role_id: '', audit_control_id: null }])
    }

    const updateRoleId = (index: number, roleId: string) => {
        setRoleEntries(prev => prev.map((r, i) => {
            if (i !== index) return r
            // Clear AC when switching to an overall role
            return { role_id: roleId, audit_control_id: requiresAuditControl(roleId) ? r.audit_control_id : null }
        }))
    }

    const updateRoleAcId = (index: number, acId: number | null) => {
        setRoleEntries(prev => prev.map((r, i) => i === index ? { ...r, audit_control_id: acId } : r))
    }

    const removeRoleEntry = (index: number) => {
        setRoleEntries(prev => prev.filter((_, i) => i !== index))
    }

    const validateRoles = (): string | null => {
        for (const entry of roleEntries) {
            if (!entry.role_id) return 'Each role entry must have a role selected'
            if (requiresAuditControl(entry.role_id)) {
                if (!entry.audit_control_id) {
                    return `Role "${entry.role_id}" requires an audit control ID`
                }
                if (!acIdSet.has(entry.audit_control_id)) {
                    return `Audit control ${entry.audit_control_id} does not belong to this company`
                }
            }
        }
        return null
    }

    const onSubmit = async () => {
        if (!email.trim()) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: 'Email is required' } }))
            return
        }
        const roleError = validateRoles()
        if (roleError) {
            dispatch(setUserMsg({ msg: { type: 'danger', txt: roleError } }))
            return
        }

        setIsSubmitting(true)
        try {
            if (isEditMode) {
                // Update user roles via the update endpoint
                await httpService.put('users/update', {
                    company_name: companyName,
                    email: email.trim(),
                    first_name: firstName.trim(),
                    last_name: lastName.trim(),
                    roles: roleEntries.filter(r => r.role_id),
                })
                dispatch(setUserMsg({ msg: { type: 'success', txt: 'User updated successfully' } }))
            } else {
                // Create DB user via existing onboarding endpoint
                await httpService.post('onboarding/setup-admin-users', {
                    company_name: companyName,
                    admin_users: [{
                        email: email.trim(),
                        first_name: firstName.trim(),
                        last_name: lastName.trim(),
                    }],
                })

                // If roles beyond the default overall-write are needed, update them
                const hasNonDefaultRoles = roleEntries.some(r => r.role_id !== 'overall-write') ||
                    roleEntries.some(r => r.audit_control_id) ||
                    !roleEntries.some(r => r.role_id === 'overall-write')
                if (hasNonDefaultRoles && roleEntries.length > 0) {
                    await httpService.put('users/update', {
                        company_name: companyName,
                        email: email.trim(),
                        first_name: firstName.trim(),
                        last_name: lastName.trim(),
                        roles: roleEntries.filter(r => r.role_id),
                    })
                }

                // Optionally create in Cognito
                let cognitoMsg = ''
                if (createInCognito) {
                    try {
                        await httpService.post('onboarding/create-cognito-user', {
                            company_name: companyName,
                            email: email.trim(),
                            password: cognitoPassword || suggestedPassword,
                        })
                        cognitoMsg = ' and Cognito'
                    } catch (cogErr: any) {
                        let errText = 'Cognito creation failed'
                        try {
                            if (cogErr?.json) {
                                const body = await cogErr.json()
                                errText = body.error || errText
                            }
                        } catch { /* ignore */ }
                        cognitoMsg = ` (Cognito failed: ${errText})`
                    }
                }

                dispatch(setUserMsg({ msg: { type: 'success', txt: `User created in DB${cognitoMsg}` } }))
            }
            onSuccess()
        } catch (err: any) {
            let errorMsg = isEditMode ? 'Failed to update user' : 'Failed to create user'
            try {
                if (err?.json) {
                    const body = await err.json()
                    errorMsg = body.error || errorMsg
                }
            } catch { /* ignore */ }
            dispatch(setUserMsg({ msg: { type: 'danger', txt: errorMsg } }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = isEditMode ? 'Edit User' : 'Create User'

    return <div ref={trapRef}>
        <section className="user-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">User Details</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText keyName="email" label="Email" isRequired={true} value={email}
                                onChange={e => setEmail(e.target.value)} isDisabled={isEditMode} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText keyName="firstName" label="First Name" value={firstName}
                                onChange={e => setFirstName(e.target.value)} />
                        </div>
                        <div className="field">
                            <FormInputText keyName="lastName" label="Last Name" value={lastName}
                                onChange={e => setLastName(e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="form-section-card">
                    <div className="form-section-label">Roles</div>
                    <div className="role-entries">
                        {roleEntries.map((entry, idx) => (
                            <div key={idx} className="role-entry-row">
                                <select
                                    className="role-select"
                                    value={entry.role_id}
                                    onChange={e => updateRoleId(idx, e.target.value)}
                                >
                                    <option value="">Select Role...</option>
                                    {AVAILABLE_ROLES.map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                                {requiresAuditControl(entry.role_id) && entry.role_id && (
                                    <select
                                        className="ac-select"
                                        value={entry.audit_control_id ?? ''}
                                        onChange={e => updateRoleAcId(idx, e.target.value ? Number(e.target.value) : null)}
                                    >
                                        <option value="">Select Audit Control...</option>
                                        {auditControls.map(ac => (
                                            <option key={ac.id} value={ac.id}>
                                                AC {ac.id} — {ac.systemName ? `${ac.systemName} / ` : ''}{ac.objectiveName} ({ac.entityName})
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <button type="button" className="ac-remove-btn" onClick={() => removeRoleEntry(idx)}
                                    title="Remove">&times;</button>
                            </div>
                        ))}
                        <button type="button" className="ac-add-btn" onClick={addRoleEntry}>
                            + Add Role
                        </button>
                    </div>
                </div>

                {!isEditMode && (
                    <div className="form-section-card">
                        <div className="form-section-label">Cognito</div>
                        <label className="role-checkbox">
                            <input
                                type="checkbox"
                                checked={createInCognito}
                                onChange={() => setCreateInCognito(!createInCognito)}
                            />
                            Also create in AWS Cognito
                        </label>
                        {createInCognito && (
                            <div className="form-row" style={{ marginTop: '0.5rem' }}>
                                <div className="field">
                                    <FormInputText keyName="cognitoPassword" label="Password" value={cognitoPassword}
                                        onChange={e => setCognitoPassword(e.target.value)}
                                        placeholder={suggestedPassword} />
                                    <span className="field-hint">Leave empty to use: {suggestedPassword}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="form-actions">
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>
                        {isEditMode ? 'Save' : 'Create'}
                    </button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
